import React from 'react';
import {
  Box as MuiBox,
  Typography as MuiTypography,
  useTheme,
} from '@mui/material';
import styled from 'styled-components';
import { Field } from 'react-final-form';
import {
  SMuiBox,
  SContainer,
  SContent,
  SHeader,
} from './components/DisplayContainer';
import { newQuestionTypes } from 'lib/enums/questionTypes';
import GetQuestionType from 'lib/utils/getQuestionType';
import { useTranslation } from 'react-i18next';
import { isJson } from 'lib/utils/WYSIWYGEditorHelper';
import { stateToHTML } from 'draft-js-export-html';
import { convertFromRaw } from 'draft-js';
import { isClockTime } from 'lib/utils/units';
import {
  TextInput,
  SelectInput,
  DateInput,
  TimeInput,
  ScaleInput,
  RatingInput,
  NPSInput,
  LikertInput,
  MediaItem,
} from 'components/questionnaire/inputs';

interface EditableQuestionnaireItemProps {
  question: any;
  response: any;
  questionState: any;
  index: number;
  isModified: boolean;
  displayIndex?: boolean;
}

const EditableQuestionnaireItem: React.FC<EditableQuestionnaireItemProps> = ({
  question,
  response,
  questionState,
  index,
  isModified,
  displayIndex = true,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const renderField = () => {
    const fieldName = `answer[${index}]`;

    if (questionState?.status !== 'ANSWERED') {
      return (
        <ResponseContainer>
          <SDiv>{t('Not answered')}</SDiv>
        </ResponseContainer>
      );
    }

    return (
      <Field name={fieldName}>
        {({ input }) => renderInput({ fieldName, input })}
      </Field>
    );
  };

  const renderInput = ({
    fieldName,
    input,
  }: {
    fieldName: string;
    input: any;
  }) => {
    const questionType = question?.questionType?.toLowerCase();

    switch (questionType) {
      case newQuestionTypes.openQuestion:
      case newQuestionTypes.text:
        return (
          <TextInput
            question={question}
            value={input.value}
            onChange={input.onChange}
          />
        );

      case newQuestionTypes.numeric:
        const unitCode = response?.metric?.components?.[0]?.quantityValue?.code;
        if (isClockTime(unitCode)) {
          return (
            <TimeInput
              question={question}
              value={input.value}
              onChange={input.onChange}
            />
          );
        }
        return (
          <TextInput
            question={question}
            value={input.value}
            onChange={input.onChange}
          />
        );

      case newQuestionTypes.choice:
      case newQuestionTypes.multipleChoice:
      case newQuestionTypes.singleChoice:
        return (
          <SelectInput
            question={question}
            value={input.value}
            onChange={input.onChange}
          />
        );

      case newQuestionTypes.scale:
        return (
          <ScaleInput
            question={question}
            value={input.value}
            onChange={input.onChange}
          />
        );

      case newQuestionTypes.nps:
        return (
          <NPSInput
            question={question}
            value={input.value}
            onChange={input.onChange}
          />
        );

      case newQuestionTypes.rating:
        return (
          <RatingInput
            question={question}
            value={input.value}
            onChange={input.onChange}
          />
        );

      case newQuestionTypes.likert:
        return (
          <LikertInput
            question={question}
            value={input.value}
            onChange={input.onChange}
          />
        );

      case newQuestionTypes.date:
        return (
          <DateInput
            question={question}
            value={input.value}
            onChange={input.onChange}
            fieldName={fieldName}
          />
        );

      case newQuestionTypes.media:
        return <MediaItem question={question} />;

      default:
        return (
          <TextInput
            question={question}
            value={input.value}
            onChange={input.onChange}
          />
        );
    }
  };

  return (
    <SMuiBox
      sx={{
        backgroundColor: isModified ? '#FFFCEB' : theme.palette.colors.gray100,
        border: isModified ? '1px solid #E1CE1E' : 'none',
      }}
    >
      <SContainer>
        <SHeader>
          <MuiTypography fontSize={13}>
            {displayIndex
              ? `Q${index + 1} - ${t(GetQuestionType(question?.questionType))}`
              : t(GetQuestionType(question?.questionType))}
          </MuiTypography>
          <MuiTypography fontSize={13}>
            {response?.score ? `${t('Score')}: ${response.score}` : ''}
          </MuiTypography>
        </SHeader>
        {response ? (
          <>
            {isJson(response?.questionText) ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: stateToHTML(
                    convertFromRaw(JSON.parse(response?.questionText))
                  ),
                }}
              />
            ) : (
              <div>{response?.questionText}</div>
            )}
            <SContent sx={{ mt: 2 }}>{renderField()}</SContent>
          </>
        ) : null}
      </SContainer>
    </SMuiBox>
  );
};

export default EditableQuestionnaireItem;

const ResponseContainer = styled(MuiBox)`
  && {
    background-color: ${({ theme }) => theme.palette.colors.white};
    padding: 5px;
    border-radius: 5px;
    border: 1px solid ${({ theme }) => theme.palette.colors.gray200};
    margin-top: 10px;
  }
`;

const SDiv = styled.div`
  && {
    margin: 0 5px;
  }
`;
